import React, { useState, useEffect } from 'react';
import { FiClock, FiShield, FiDollarSign, FiLayers, FiEye, FiFileText, FiPrinter, FiShoppingCart, FiImage, FiColumns, FiRotateCw, FiUploadCloud } from 'react-icons/fi';
import FileUpload from './FileUpload';
import API from '../api/axios';
import './PrintStoreLanding.css';

const PrintStoreLanding = ({ onFilesUploaded }) => {
  const [rates, setRates] = useState({ bw: 3, colour: 10 });

  useEffect(() => {
    const fetchRates = async () => {
      try {
        const { data } = await API.get('/print-rates');
        if (data.success && data.rates) {
          const bw = data.rates.find(r => r.key === 'file_bw')?.value ?? 3;
          const colour = data.rates.find(r => r.key === 'file_color')?.value ?? 10;
          setRates({ bw, colour });
        }
      } catch (err) {
        console.warn('Failed to fetch print rates in PrintStoreLanding:', err);
      }
    };
    fetchRates();
  }, []);

  return (
    <div className="print-store-landing">

      {/* Hero Banner */}
      <div className="ps-hero">
        <div className="ps-hero-illustration">
          <span className="ps-hero-emoji">🖨️</span>
          <div className="ps-hero-docs">
            <span>📄</span><span>📑</span><span>🖼️</span>
          </div>
        </div>
        <h2 className="ps-hero-title">Print Store</h2>
        <p className="ps-hero-subtitle">Shubham Computers provides Safe & Secure printouts</p>
      </div>

      {/* NCR Prints Studio Instant Quote Banner */}
      <div
        className="ps-quote-studio-banner"
        onClick={() => window.dispatchEvent(new CustomEvent('open-print-quote'))}
        style={{
          margin: '0 0 16px 0',
          padding: '14px 18px',
          background: 'linear-gradient(135deg, rgba(108, 99, 255, 0.22) 0%, rgba(255, 183, 77, 0.12) 100%)',
          border: '1px solid rgba(108, 99, 255, 0.35)',
          borderRadius: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          gap: '12px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '1.6rem' }}>💎</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: '0.92rem', color: '#ffffff' }}>
              UV DTF, Acrylic &amp; Glass Quote Studio
            </div>
            <div style={{ fontSize: '0.74rem', color: '#b0b4ce' }}>
              Instant live sq.in rates, 3D raised finishes &amp; WhatsApp quotation
            </div>
          </div>
        </div>
        <button
          type="button"
          style={{
            background: 'linear-gradient(135deg, #6C63FF 0%, #5a52d5 100%)',
            color: '#ffffff',
            border: 'none',
            padding: '7px 12px',
            borderRadius: '8px',
            fontWeight: 700,
            fontSize: '0.75rem',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
        >
          Get Quote ➔
        </button>
      </div>

      {/* Functional File Upload */}
      <FileUpload onFilesUploaded={onFilesUploaded} />

      {/* Why Try Print Store */}
      <div className="ps-section">
        <h3 className="ps-section-title">Why try our Print Store?</h3>
        <div className="ps-features-grid">
          <div className="ps-feature-card">
            <div className="ps-feature-icon clock">
              <FiClock size={20} />
            </div>
            <h4>Delivery in minutes</h4>
            <p>Instant deliveries under 30 minutes</p>
          </div>
          <div className="ps-feature-card">
            <div className="ps-feature-icon shield">
              <FiShield size={20} />
            </div>
            <h4>Safe and secure</h4>
            <p>We delete your files once delivered</p>
          </div>
          <div className="ps-feature-card">
            <div className="ps-feature-icon dollar">
              <FiDollarSign size={20} />
            </div>
            <h4>Affordable Printing</h4>
            <p>B&W: ₹{rates.bw} per page<br />Colour: ₹{rates.colour} per page</p>
          </div>
          <div className="ps-feature-card">
            <div className="ps-feature-icon layers">
              <FiLayers size={20} />
            </div>
            <h4>No minimum order</h4>
            <p>Order as many pages as few as you want</p>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="ps-section ps-how-section">
        <h3 className="ps-section-title">How Print Store works</h3>
        <p className="ps-section-desc">Let Shubham Computers take care of your everyday printing needs</p>
        <div className="ps-steps">
          <div className="ps-step">
            <div className="ps-step-content">
              <h4>Visit Print Store</h4>
              <p>Open the app and browse print services</p>
            </div>
            <div className="ps-step-icon visit"><FiEye size={18} /></div>
          </div>
          <div className="ps-step">
            <div className="ps-step-content">
              <h4>Upload file(s)</h4>
              <p>Upload a file or multiple files to take prints</p>
            </div>
            <div className="ps-step-icon upload"><FiUploadCloud size={18} /></div>
          </div>
          <div className="ps-step">
            <div className="ps-step-content">
              <h4>Customise print</h4>
              <p>Choose print settings as per your requirement</p>
            </div>
            <div className="ps-step-icon customize"><FiPrinter size={18} /></div>
          </div>
          <div className="ps-step">
            <div className="ps-step-content">
              <h4>Checkout</h4>
              <p>Add prints to cart and place an order</p>
            </div>
            <div className="ps-step-icon checkout"><FiShoppingCart size={18} /></div>
          </div>
        </div>
      </div>

      {/* Customisation Options */}
      <div className="ps-section ps-custom-section">
        <h3 className="ps-section-title">Customisation options</h3>
        <div className="ps-options-list">
          <div className="ps-option">
            <div className="ps-option-icon"><FiFileText size={20} /></div>
            <div>
              <h4>Upload any file type</h4>
              <p>Print PDF, JPG, PNG, JPEG, and many more</p>
            </div>
          </div>
          <div className="ps-option">
            <div className="ps-option-icon"><FiImage size={20} /></div>
            <div>
              <h4>Black & White / Colour</h4>
              <p>Save cost with B&W or pick the coloured option</p>
            </div>
          </div>
          <div className="ps-option">
            <div className="ps-option-icon"><FiColumns size={20} /></div>
            <div>
              <h4>Paper format size</h4>
              <p>We work with A4 printing paper of 70 GSM</p>
            </div>
          </div>
          <div className="ps-option">
            <div className="ps-option-icon"><FiRotateCw size={20} /></div>
            <div>
              <h4>Orientation</h4>
              <p>Choose landscape or portrait as per your need</p>
            </div>
          </div>
        </div>
      </div>

      {/* Testimonials */}
      <div className="ps-section ps-testimonials-section">
        <h3 className="ps-section-title">Proudly serving 500+ happy customers</h3>
        <p className="ps-section-desc">We think our Print Store is awesome. But don't take our word for it!</p>
        <div className="ps-testimonials-label">Testimonials</div>
        <div className="ps-testimonials-grid">
          <div className="ps-testimonial">
            <div className="ps-testimonial-avatar">R</div>
            <h4>Rahul Sharma</h4>
            <p>Super fast delivery! Got my project printed in under 20 minutes. Life saver during exams.</p>
          </div>
          <div className="ps-testimonial">
            <div className="ps-testimonial-avatar">P</div>
            <h4>Priya Yadav</h4>
            <p>Affordable and quality printing right at my doorstep. The B&W prints are super clear.</p>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PrintStoreLanding;
